import { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faMoneyBill, faCreditCard, faMobileScreen, faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./PaymentsPage.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";
const EGP = (n: number) => `EGP ${(n ?? 0).toLocaleString()}`;

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

interface BookingPayment {
  id: string;
  bookingId: string;
  guestName: string;
  roomId: string;
  checkIn: string | null;
  checkOut: string | null;
  nights: number;
  roomPrice: number;
  orderPrice: number;
  total: number;
  deposit: number;
  paid: number;
  remaining: number;
  paymentStatus: "unpaid" | "partial" | "paid";
  bookingStatus: string;
}

interface PaymentRecord {
  id: string;
  bookingid: string;
  amount: number;
  method: string;
  card_number?: string;
  transaction_ref?: string;
  note: string;
  created_at: string;
}

type FilterTab = "All" | "Unpaid" | "Partial" | "Paid";

const STATUS_COLORS: Record<string, string> = {
  paid:    "#34d399",
  partial: "#fbbf24",
  unpaid:  "#ef4444",
};

export default function PaymentsPage() {
  const [bookings, setBookings] = useState<BookingPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<FilterTab>("All");
  const [search, setSearch] = useState("");
  const [selectedBooking, setSelectedBooking] = useState<BookingPayment | null>(null);
  const [records, setRecords] = useState<PaymentRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [toast, setToast] = useState("");

  // Pay modal state
  const [showPayModal, setShowPayModal] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState<"cash" | "visa" | "instapay">("cash");
  const [payCardNumber, setPayCardNumber] = useState("");
  const [payTransactionRef, setPayTransactionRef] = useState("");
  const [payNote, setPayNote] = useState("");
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");

  const [sortCol, setSortCol] = useState<keyof BookingPayment>("checkIn");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const toggleSort = (col: keyof BookingPayment) => {
    if (sortCol === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  // Edit prices modal
  const [showPricesModal, setShowPricesModal] = useState(false);
  const [editRoomPrice, setEditRoomPrice] = useState("");
  const [editOrderPrice, setEditOrderPrice] = useState("");
  const [savingPrices, setSavingPrices] = useState(false);

  const loadBookings = () => {
    setLoading(true);
    axios.get(`${API_BASE}/api/dashboard/payments`)
      .then((r) => setBookings(r.data || []))
      .catch(() => setError("Failed to load payments"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadBookings(); }, []);

  const openBooking = async (b: BookingPayment) => {
    setSelectedBooking(b);
    setRecordsLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/api/dashboard/payments/${b.id}/records`);
      setRecords(r.data || []);
    } catch { setRecords([]); }
    finally { setRecordsLoading(false); }
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const handlePay = async () => {
    if (!selectedBooking) return;
    setPayError("");
    if (!payAmount || Number(payAmount) <= 0) { setPayError("Amount is required and must be greater than 0."); return; }
    if (payMethod === "visa" && !payCardNumber.trim()) { setPayError("Card number (last 4 digits) is required for Visa payments."); return; }
    if (payMethod === "instapay" && !payTransactionRef.trim()) { setPayError("Transaction reference is required for Instapay payments."); return; }
    setPaying(true);
    try {
      const r = await axios.post(`${API_BASE}/api/dashboard/payments/${selectedBooking.bookingId}/pay`, {
        amount: Number(payAmount),
        method: payMethod,
        card_number: payMethod === "visa" ? payCardNumber : null,
        transaction_ref: payMethod === "instapay" ? payTransactionRef : null,
        note: payNote,
        bookingRoomId: selectedBooking.id,
      });
      setBookings((prev) => prev.map((b) =>
        b.id === selectedBooking.id
          ? { ...b, paid: r.data.paid, remaining: r.data.remaining, paymentStatus: r.data.paymentStatus }
          : b
      ));
      setSelectedBooking((s) => s ? { ...s, paid: r.data.paid, remaining: r.data.remaining, paymentStatus: r.data.paymentStatus } : s);
      const newRec: PaymentRecord = {
        id: Date.now().toString(),
        bookingid: selectedBooking.id,
        amount: Number(payAmount),
        method: payMethod,
        card_number: payMethod === "visa" ? payCardNumber : undefined,
        transaction_ref: payMethod === "instapay" ? payTransactionRef : undefined,
        note: payNote,
        created_at: new Date().toISOString(),
      };
      setRecords((prev) => [newRec, ...prev]);
      setPayAmount(""); setPayNote(""); setPayMethod("cash");
      setPayCardNumber(""); setPayTransactionRef(""); setPayError("");
      setShowPayModal(false);
      showToast(`Payment of ${EGP(Number(payAmount))} recorded`);
    } catch {
      showToast("Payment failed. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  const handleSavePrices = async () => {
    if (!selectedBooking) return;
    setSavingPrices(true);
    try {
      await axios.put(`${API_BASE}/api/dashboard/payments/${selectedBooking.id}/prices`, {  // id = booking_room.id
        room_price: Number(editRoomPrice),
        order_price: Number(editOrderPrice),
      });
      const newTotal = Number(editRoomPrice) + Number(editOrderPrice);
      setBookings((prev) => prev.map((b) =>
        b.id === selectedBooking.id
          ? { ...b, roomPrice: Number(editRoomPrice), orderPrice: Number(editOrderPrice), total: newTotal, remaining: Math.max(0, newTotal - b.paid) }
          : b
      ));
      setSelectedBooking((s) => s ? {
        ...s,
        roomPrice: Number(editRoomPrice),
        orderPrice: Number(editOrderPrice),
        total: newTotal,
        remaining: Math.max(0, newTotal - s.paid),
      } : s);
      setShowPricesModal(false);
      showToast("Prices updated");
    } catch {
      showToast("Failed to update prices");
    } finally {
      setSavingPrices(false);
    }
  };

  const filtered = bookings.filter((b) => {
    const matchFilter = filter === "All" || b.paymentStatus === filter.toLowerCase();
    const matchSearch = !search || b.guestName.toLowerCase().includes(search.toLowerCase()) || b.roomId.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const sorted = [...filtered].sort((a, b) => {
    let av: any = a[sortCol];
    let bv: any = b[sortCol];
    if (av == null) av = "";
    if (bv == null) bv = "";
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const totalPaid = bookings.reduce((s, b) => s + b.paid, 0);
  const totalOutstanding = bookings.reduce((s, b) => s + b.remaining, 0);
  const totalRevenue = bookings.reduce((s, b) => s + b.total, 0);
  const depositsOutstanding = bookings.reduce(
    (s, b) => b.paymentStatus !== "paid" ? s + Math.max(0, (b.deposit || 0) - b.paid) : s,
    0
  );

  const filterTabs: FilterTab[] = ["All", "Unpaid", "Partial", "Paid"];

  return (
    <DashboardLayout>
      <div className="pp-page">
        {/* Header */}
        <div className="pp-header">
          <div>
            <h1 className="pp-title">Payments</h1>
            <p className="pp-subtitle">Track room charges, orders, and payments</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="pp-stats-row">
          <div className="pp-stat-card">
            <p className="pp-stat-label">Total Revenue</p>
            <p className="pp-stat-value">{EGP(totalRevenue)}</p>
          </div>
          <div className="pp-stat-card">
            <p className="pp-stat-label">Collected</p>
            <p className="pp-stat-value pp-stat-green">{EGP(totalPaid)}</p>
          </div>
          <div className="pp-stat-card">
            <p className="pp-stat-label">Outstanding</p>
            <p className="pp-stat-value pp-stat-red">{EGP(totalOutstanding)}</p>
          </div>
          <div className="pp-stat-card">
            <p className="pp-stat-label">Deposits Outstanding</p>
            <p className="pp-stat-value pp-stat-gold">{EGP(depositsOutstanding)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="pp-toolbar">
          <div className="pp-filter-tabs">
            {filterTabs.map((t) => (
              <button key={t} className={`pp-filter-tab ${filter === t ? "active" : ""}`} onClick={() => setFilter(t)}>
                {t}
                <span className="pp-filter-count">
                  {t === "All" ? bookings.length : bookings.filter((b) => b.paymentStatus === t.toLowerCase()).length}
                </span>
              </button>
            ))}
          </div>
          <input
            className="pp-search"
            placeholder="Search guest or room…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading && <div className="pp-loading">Loading payments…</div>}
        {error && <div className="pp-error">{error}</div>}

        {/* Table */}
        {!loading && !error && (
          <div className="pp-table-card">
            {filtered.length === 0 ? (
              <div className="pp-empty">No bookings found</div>
            ) : (
              <table className="pp-table">
                <thead>
                  <tr>
                    {([
                      { col: "guestName" as keyof BookingPayment, label: "Guest", right: false },
                      { col: "roomId" as keyof BookingPayment, label: "Room", right: false },
                      { col: "checkIn" as keyof BookingPayment, label: "Dates", right: false },
                      { col: "roomPrice" as keyof BookingPayment, label: "Room Price", right: true },
                      { col: "orderPrice" as keyof BookingPayment, label: "Orders", right: true },
                      { col: "total" as keyof BookingPayment, label: "Total", right: true },
                      { col: "deposit" as keyof BookingPayment, label: "Deposit", right: true },
                      { col: "paid" as keyof BookingPayment, label: "Paid", right: true },
                      { col: "remaining" as keyof BookingPayment, label: "Remaining", right: true },
                      { col: "paymentStatus" as keyof BookingPayment, label: "Status", right: false },
                    ] as const).map(({ col, label, right }) => (
                      <th
                        key={col}
                        className={`pp-th pp-th-sortable${right ? " pp-th-right" : ""}${sortCol === col ? " pp-th-active" : ""}`}
                        onClick={() => toggleSort(col)}
                      >
                        {label}
                        <span className="pp-sort-icon">
                          {sortCol === col
                            ? <FontAwesomeIcon icon={sortDir === "asc" ? faChevronUp : faChevronDown} />
                            : <FontAwesomeIcon icon={faChevronDown} style={{ opacity: 0.25 }} />}
                        </span>
                      </th>
                    ))}
                    <th className="pp-th"></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((b) => (
                    <tr key={b.id} className="pp-tr" onClick={() => openBooking(b)}>
                      <td className="pp-td pp-td-bold">{b.guestName}</td>
                      <td className="pp-td"><span className="pp-room-badge">{b.roomId}</span></td>
                      <td className="pp-td pp-td-muted">{fmt(b.checkIn)} → {fmt(b.checkOut)}</td>
                      <td className="pp-td pp-td-right">{EGP(b.roomPrice)}</td>
                      <td className="pp-td pp-td-right">{EGP(b.orderPrice)}</td>
                      <td className="pp-td pp-td-right pp-td-total">{EGP(b.total)}</td>
                      <td className="pp-td pp-td-right pp-td-deposit">
                        {EGP(b.deposit)}
                        {b.deposit > 0 && b.paid < b.deposit && (
                          <span className="pp-deposit-due">deposit due</span>
                        )}
                      </td>
                      <td className="pp-td pp-td-right pp-td-paid">{EGP(b.paid)}</td>
                      <td className="pp-td pp-td-right" style={{ color: b.remaining > 0 ? "#ef4444" : "#34d399" }}>
                        {EGP(b.remaining)}
                      </td>
                      <td className="pp-td">
                        <span className="pp-status-badge" style={{ color: STATUS_COLORS[b.paymentStatus], background: STATUS_COLORS[b.paymentStatus] + "22", borderColor: STATUS_COLORS[b.paymentStatus] + "44" }}>
                          {b.paymentStatus}
                        </span>
                      </td>
                      <td className="pp-td pp-td-action" onClick={(e) => e.stopPropagation()}>
                        <button className="pp-pay-btn" onClick={() => { setSelectedBooking(b); openBooking(b); setShowPayModal(true); setPayAmount(String(b.remaining)); }}>
                          + Pay
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Booking Detail Drawer */}
      {selectedBooking && !showPayModal && !showPricesModal && (
        <div className="pp-overlay" onClick={() => setSelectedBooking(null)}>
          <div className="pp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="pp-drawer-header">
              <div>
                <h2 className="pp-drawer-name">{selectedBooking.guestName}</h2>
                <p className="pp-drawer-sub">Room {selectedBooking.roomId} · {selectedBooking.nights} nights</p>
              </div>
              <button className="pp-drawer-close" onClick={() => setSelectedBooking(null)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>

            {/* Price Breakdown */}
            <div className="pp-breakdown">
              <div className="pp-breakdown-row">
                <span className="pp-breakdown-label">Room Price</span>
                <span className="pp-breakdown-val">{EGP(selectedBooking.roomPrice)}</span>
              </div>
              <div className="pp-breakdown-row">
                <span className="pp-breakdown-label">Orders / Extras</span>
                <span className="pp-breakdown-val">{EGP(selectedBooking.orderPrice)}</span>
              </div>
              <div className="pp-breakdown-row pp-breakdown-total">
                <span className="pp-breakdown-label">Total</span>
                <span className="pp-breakdown-val">{EGP(selectedBooking.total)}</span>
              </div>
              {selectedBooking.deposit > 0 && (
                <div className="pp-breakdown-row">
                  <span className="pp-breakdown-label">Deposit due</span>
                  <span className="pp-breakdown-val pp-val-gold">{EGP(selectedBooking.deposit)}</span>
                </div>
              )}
              <div className="pp-breakdown-row">
                <span className="pp-breakdown-label">Paid</span>
                <span className="pp-breakdown-val pp-val-green">{EGP(selectedBooking.paid)}</span>
              </div>
              <div className="pp-breakdown-row">
                <span className="pp-breakdown-label">Remaining</span>
                <span className="pp-breakdown-val" style={{ color: selectedBooking.remaining > 0 ? "#ef4444" : "#34d399" }}>
                  {EGP(selectedBooking.remaining)}
                </span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="pp-progress-wrap">
              <div className="pp-progress-bar">
                <div
                  className="pp-progress-fill"
                  style={{ width: selectedBooking.total > 0 ? `${Math.min(100, (selectedBooking.paid / selectedBooking.total) * 100)}%` : "0%" }}
                />
              </div>
              <span className="pp-progress-label">
                {selectedBooking.total > 0 ? Math.round((selectedBooking.paid / selectedBooking.total) * 100) : 0}% paid
              </span>
            </div>

            {/* Action buttons */}
            <div className="pp-drawer-actions">
              <button className="pp-drawer-pay-btn" onClick={() => { setPayAmount(String(selectedBooking.remaining)); setShowPayModal(true); }}>
                + Add Payment
              </button>
              <button className="pp-drawer-edit-btn" onClick={() => { setEditRoomPrice(String(selectedBooking.roomPrice)); setEditOrderPrice(String(selectedBooking.orderPrice)); setShowPricesModal(true); }}>
                Edit Prices
              </button>
            </div>

            {/* Payment History */}
            <div className="pp-records">
              <h3 className="pp-records-title">Payment History</h3>
              {recordsLoading ? (
                <div className="pp-records-loading">Loading…</div>
              ) : records.length === 0 ? (
                <div className="pp-records-empty">No payments recorded yet</div>
              ) : (
                records.map((r) => (
                  <div key={r.id} className="pp-record-row">
                    <div className="pp-record-left">
                      <span className="pp-record-method">{r.method === "visa" ? <><FontAwesomeIcon icon={faCreditCard} /> Visa</> : r.method === "instapay" ? <><FontAwesomeIcon icon={faMobileScreen} /> Instapay</> : <><FontAwesomeIcon icon={faMoneyBill} /> Cash</>}</span>
                      {r.method === "visa" && r.card_number && <span className="pp-record-note">•••• {r.card_number}</span>}
                      {r.method === "instapay" && r.transaction_ref && <span className="pp-record-note">Ref: {r.transaction_ref}</span>}
                      {r.note && <span className="pp-record-note">{r.note}</span>}
                    </div>
                    <div className="pp-record-right">
                      <span className="pp-record-amount">{EGP(r.amount)}</span>
                      <span className="pp-record-date">{new Date(r.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pay Modal */}
      {showPayModal && selectedBooking && (
        <div className="pp-overlay" onClick={() => setShowPayModal(false)}>
          <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pp-modal-header">
              <h3 className="pp-modal-title">Add Payment</h3>
              <button className="pp-modal-close" onClick={() => setShowPayModal(false)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <p className="pp-modal-sub">{selectedBooking.guestName} · Room {selectedBooking.roomId}</p>

            {/* At-a-glance amounts */}
            <div className="pp-modal-amounts">
              <div className="pp-amt-box">
                <span className="pp-amt-label">Total</span>
                <span className="pp-amt-val">{EGP(selectedBooking.total)}</span>
              </div>
              <div className="pp-amt-box">
                <span className="pp-amt-label">Paid</span>
                <span className="pp-amt-val pp-val-green">{EGP(selectedBooking.paid)}</span>
              </div>
              <div className="pp-amt-box pp-amt-box-hl">
                <span className="pp-amt-label">Remaining</span>
                <span className="pp-amt-val" style={{ color: selectedBooking.remaining > 0 ? "#ef4444" : "#34d399" }}>{EGP(selectedBooking.remaining)}</span>
              </div>
            </div>

            {/* Quick fill chips */}
            <div className="pp-quick-chips">
              {selectedBooking.deposit > 0 && selectedBooking.paid < selectedBooking.deposit && (
                <button
                  type="button"
                  className="pp-chip pp-chip-gold"
                  onClick={() => setPayAmount(String(Math.max(0, selectedBooking.deposit - selectedBooking.paid)))}
                >
                  Deposit · {EGP(Math.max(0, selectedBooking.deposit - selectedBooking.paid))}
                </button>
              )}
              <button type="button" className="pp-chip" onClick={() => setPayAmount(String(selectedBooking.remaining))}>
                Remaining · {EGP(selectedBooking.remaining)}
              </button>
              {[0.5, 0.25].map((f) => (
                <button key={f} type="button" className="pp-chip" onClick={() => setPayAmount(String(Math.round(selectedBooking.remaining * f)))}>
                  {f * 100}%
                </button>
              ))}
            </div>

            {/* Payment Method — 3 buttons */}
            <div className="pp-modal-field">
              <label className="pp-modal-label">Payment Method</label>
              <div className="pp-method-btns">
                {(["cash", "visa", "instapay"] as const).map(m => (
                  <button
                    key={m}
                    className={`pp-method-btn ${payMethod === m ? "active" : ""}`}
                    onClick={() => { setPayMethod(m); setPayCardNumber(""); setPayTransactionRef(""); setPayError(""); }}
                  >
                    {m === "cash" ? <><FontAwesomeIcon icon={faMoneyBill} /> Cash</> : m === "visa" ? <><FontAwesomeIcon icon={faCreditCard} /> Visa</> : <><FontAwesomeIcon icon={faMobileScreen} /> Instapay</>}
                  </button>
                ))}
              </div>
            </div>

            <div className="pp-modal-field">
              <label className="pp-modal-label">Amount (EGP)</label>
              <input
                className="pp-modal-input"
                type="number"
                min="1"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </div>

            {/* Visa — card number */}
            {payMethod === "visa" && (
              <div className="pp-modal-field">
                <label className="pp-modal-label">Card Number (last 4 digits)</label>
                <input
                  className="pp-modal-input"
                  type="text"
                  maxLength={4}
                  value={payCardNumber}
                  onChange={(e) => setPayCardNumber(e.target.value)}
                  placeholder="e.g. 4242"
                />
              </div>
            )}

            {/* Instapay — transaction ref */}
            {payMethod === "instapay" && (
              <div className="pp-modal-field">
                <label className="pp-modal-label">Transaction Reference</label>
                <input
                  className="pp-modal-input"
                  type="text"
                  value={payTransactionRef}
                  onChange={(e) => setPayTransactionRef(e.target.value)}
                  placeholder="Transaction ID / reference number"
                />
              </div>
            )}

            <div className="pp-modal-field">
              <label className="pp-modal-label">Note (optional)</label>
              <input
                className="pp-modal-input"
                value={payNote}
                onChange={(e) => setPayNote(e.target.value)}
                placeholder="e.g. deposit, partial payment…"
              />
            </div>
            {payError && (
              <div className="pp-modal-error">{payError}</div>
            )}
            <div className="pp-modal-footer">
              <button className="pp-modal-cancel" onClick={() => { setShowPayModal(false); setPayError(""); }}>Cancel</button>
              <button className="pp-modal-confirm" disabled={paying} onClick={handlePay}>
                {paying ? "Processing…" : `Record ${payAmount ? EGP(Number(payAmount)) : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Prices Modal */}
      {showPricesModal && selectedBooking && (
        <div className="pp-overlay" onClick={() => setShowPricesModal(false)}>
          <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pp-modal-header">
              <h3 className="pp-modal-title">Edit Prices</h3>
              <button className="pp-modal-close" onClick={() => setShowPricesModal(false)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <p className="pp-modal-sub">{selectedBooking.guestName} · Room {selectedBooking.roomId}</p>
            <div className="pp-modal-field">
              <label className="pp-modal-label">Room Price (EGP)</label>
              <input className="pp-modal-input" type="number" min="0" value={editRoomPrice} onChange={(e) => setEditRoomPrice(e.target.value)} />
            </div>
            <div className="pp-modal-field">
              <label className="pp-modal-label">Orders / Extras (EGP)</label>
              <input className="pp-modal-input" type="number" min="0" value={editOrderPrice} onChange={(e) => setEditOrderPrice(e.target.value)} />
            </div>
            <p className="pp-modal-total-preview">
              New Total: <strong>{EGP((Number(editRoomPrice) || 0) + (Number(editOrderPrice) || 0))}</strong>
            </p>
            <div className="pp-modal-footer">
              <button className="pp-modal-cancel" onClick={() => setShowPricesModal(false)}>Cancel</button>
              <button className="pp-modal-confirm" disabled={savingPrices} onClick={handleSavePrices}>
                {savingPrices ? "Saving…" : "Save Prices"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="pp-toast">{toast}</div>}
    </DashboardLayout>
  );
}
